/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";

import axios from "axios";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import {
  CREATE_EMAIL_TEMPLATE,
  DELETE_EMAIL_TEMPLATE,
  GET_EMAIL_TEMPLATES,
  UPDATE_EMAIL_TEMPLATE,
} from "../graphql";
import { VApplication } from "../../../application";
import { LoggerModule } from "@vlinder-web/logger-module-react";

import config from "../config/templateComp.config.json";

const log: any = LoggerModule?.getInstance();
const is = require("is_js");

export interface DeleteEmailTemplateProps {
  name: string;
  id: string;
}

export interface CreateEmailTemplateProps {
  formData: {
    subject: string;
    html: string;
    json: any;
  };
  name: string;
}

export interface UpdateEmailTemplateProps {
  formData: {
    subject: string;
    html: string;
    json: any;
  };
  name: string;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export interface IInviteEmailTemplatesMethods extends IPluginMethodMap {
  getEmailTemplates(args: any): Promise<any>;
  createEmailTemplate(args: any): Promise<any>;
  updateEmailTemplate(args: any): Promise<any>;
  deleteEmailTemplate(args: any): Promise<any>;
}

export class IInviteEmailTemplateService implements IAgentPlugin {
  readonly methods: IInviteEmailTemplatesMethods;

  constructor() {
    this.methods = {
      getEmailTemplates: this.getEmailTemplates.bind(this),
      createEmailTemplate: this.createEmailTemplate.bind(this),
      updateEmailTemplate: this.updateEmailTemplate.bind(this),
      deleteEmailTemplate: this.deleteEmailTemplate.bind(this),
    };
  }

  async getEmailTemplates(args: any) {
    log.blue(`getEmailTemplates server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");

    try {
      if (client) {
        const res = await client.query({
          query: GET_EMAIL_TEMPLATES,
          variables: {
            filter: {
              name: args.name,
            },
          },
          fetchPolicy: "network-only",
        });
        console.log("getEmailTemplates======", res?.data);

        return {
          ...(res.data?.getEmailTemplates?.content[0] || {}),
          tweetyProps: await this.formatFormProps(
            config.TWEETY_PROPS,
            res.data?.getEmailTemplates?.content[0]
          ),
        };
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  private async formatFormProps(formProps: any, data: any) {
    let _res: any = formProps;

    _res.initialData = {
      subject: data?.subject,
    };
    return _res;
  }
  async createEmailTemplate(args: CreateEmailTemplateProps) {
    log.blue(`createEmailTemplate server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: CREATE_EMAIL_TEMPLATE, // ----
          variables: {
            data: {
              name: args.name,
              ...args?.formData,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.createEmailTemplate,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }

  async updateEmailTemplate(args: UpdateEmailTemplateProps) {
    log.blue(`updateEmailTemplate server component:`, args);
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: UPDATE_EMAIL_TEMPLATE,
          variables: {
            filter: {
              name: args?.name,
            },
            update: {
              ...args?.formData,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data?.updateEmailTemplate,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
  async deleteEmailTemplate(args: DeleteEmailTemplateProps) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const res = await client.mutate({
          mutation: DELETE_EMAIL_TEMPLATE,
          variables: {
            filter: {
              name: args?.name,
            },
          },
          fetchPolicy: "no-cache",
        });
        if (res?.data) {
          return {
            ...res?.data,
          };
        }
      }
    } catch (err: any) {
      let _display_message =
        err?.graphQLErrors &&
        Array.isArray(err?.graphQLErrors) &&
        err?.graphQLErrors.find((item: any) => item.extensions);
      if (_display_message?.extensions?.display_message) {
        throw Error(_display_message?.extensions?.display_message);
      }

      throw err;
    }
  }
}
